import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

import { flushSync } from 'react-dom';

import type { ContextEntry, ContextMetadata, ContextFilter, ContextPriority, ContextType } from './types';

export type DebugLogEntry = {
  category: string;
  data?: any;
  duration?: number;
  id: string;
  level: 'error' | 'info' | 'log' | 'warn';
  message: string;
  parentLogId?: string;
  requestId?: string;
  status?: 'error' | 'progress' | 'start' | 'success';
  timestamp: number;
};

type DutchieIntelligenceContextType = {
  clearDebugLogs: () => void;
  clearInitialMessage: () => void;
  clearStaleContexts: () => void;
  closeIntelligence: () => void;
  // Page management
  currentPage: string | null;

  // Debug logging
  debugLog: (
    level: DebugLogEntry['level'],
    category: DebugLogEntry['category'],
    message: DebugLogEntry['message'],
    data?: DebugLogEntry['data'],
    options?: {
      duration?: DebugLogEntry['duration'];
      parentLogId?: DebugLogEntry['parentLogId'];
      requestId?: DebugLogEntry['requestId'];
      status?: DebugLogEntry['status'];
    }
  ) => void;
  // Debug helpers
  debugMode: boolean;
  evictOldestPage: () => void;
  getAllContext: (filter?: ContextFilter) => Record<string, string>;

  getCacheStats: () => { pageCount: number; pages: string[]; totalSize: number };
  getContextMetadata: () => Record<string, ContextEntry>;
  getDebugLogs: () => DebugLogEntry[];

  getPageCount: () => number;
  // Cache management
  getTotalCacheSize: () => number;
  initialMessage: string | null;
  isChatOpen: boolean;

  // AI reference tracking
  markContextsAsReferenced: (contextIds: string[]) => void;
  openIntelligenceWithMessage: (message: string) => void;

  removeComponentContext: (componentId: string) => void;
  // Enhanced context methods
  setComponentContext: (
    componentId: string,
    context: string,
    prefix?: string,
    metadata?: Partial<ContextMetadata>
  ) => void;
  setCurrentPage: (page: string) => void;

  setDebugMode: (enabled: boolean) => void;
};

const DutchieIntelligenceContext = createContext<DutchieIntelligenceContextType | undefined>(undefined);

const PRIORITY_VALUES: Record<ContextPriority, number> = {
  low: 1,
  medium: 2,
  high: 3,
};

/**
 * Configuration for context cache management.
 * Generous limits to support large datasets across multiple pages.
 */
export const CONTEXT_CACHE_CONFIG = {
  /** Maximum total cache size across all pages (5MB) */
  maxTotalSize: 5 * 1024 * 1024,
  /** Only check size after this many pages visited */
  maxPagesBeforeSizeCheck: 20,
};

export function DutchieIntelligenceProvider({ children }: { children: React.ReactNode }) {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string | null>(null);
  const [componentContexts, setComponentContexts] = useState<Record<string, ContextEntry>>({});
  const [debugMode, setDebugMode] = useState(true);
  const [currentPage, setCurrentPage] = useState<string | null>(null);
  const [debugLogs, setDebugLogs] = useState<DebugLogEntry[]>([]);

  const openIntelligenceWithMessage = useCallback((message: string) => {
    setInitialMessage(message);
    setIsChatOpen(true);
  }, []);

  const closeIntelligence = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  const clearInitialMessage = useCallback(() => {
    setInitialMessage(null);
  }, []);

  const setComponentContext = useCallback(
    (componentId: string, context: string, prefix?: string, metadataOverrides?: Partial<ContextMetadata>) => {
      const metadata: ContextMetadata = {
        id: componentId,
        scope: prefix,
        timestamp: Date.now(),
        size: context.length,
        priority: 'medium',
        type: 'other' as ContextType,
        source: 'manual', // Default source
        page: currentPage || undefined, // Auto-add current page
        ...metadataOverrides,
      };

      setComponentContexts((prev) => ({
        ...prev,
        [componentId]: { context, metadata },
      }));
    },
    [debugMode, currentPage]
  );

  const removeComponentContext = useCallback(
    (componentId: string) => {
      setComponentContexts((prev) => {
        // Use destructuring to omit the property instead of delete
        const { [componentId]: _removed, ...updated } = prev;
        return updated;
      });
    },
    [debugMode]
  );

  const getAllContext = useCallback(
    (filter?: ContextFilter) => {
      let entries = Object.entries(componentContexts);

      // Filter to current page only (unless global or ignorePageFilter is true)
      if (!filter?.ignorePageFilter) {
        entries = entries.filter(([, entry]) => {
          // Always include global contexts
          if (entry.metadata.global) {
            return true;
          }
          // Include contexts for the current page
          if (entry.metadata.page === currentPage) {
            return true;
          }
          // Exclude contexts from other pages
          return false;
        });
      }

      // Apply additional filters
      if (filter) {
        entries = entries.filter(([, entry]) => {
          const { metadata } = entry;

          // Filter by scope
          if (filter.scopes && metadata.scope && !filter.scopes.includes(metadata.scope)) {
            return false;
          }

          // Filter by type
          if (filter.types && metadata.type && !filter.types.includes(metadata.type)) {
            return false;
          }

          // Filter by priority
          if (filter.minPriority && metadata.priority) {
            if (PRIORITY_VALUES[metadata.priority] < PRIORITY_VALUES[filter.minPriority]) {
              return false;
            }
          }

          // Check TTL expiration
          if (metadata.ttl) {
            const age = Date.now() - metadata.timestamp;
            if (age > metadata.ttl) {
              return false;
            }
          }

          return true;
        });

        // Sort by priority (highest first)
        entries.sort((a, b) => {
          const priorityA = a[1].metadata.priority ? PRIORITY_VALUES[a[1].metadata.priority] : 2;
          const priorityB = b[1].metadata.priority ? PRIORITY_VALUES[b[1].metadata.priority] : 2;
          return priorityB - priorityA;
        });

        // Apply size limit
        if (filter.maxSize) {
          let totalSize = 0;
          entries = entries.filter(([, entry]) => {
            totalSize += entry.metadata.size;
            return totalSize <= filter.maxSize!;
          });
        }
      }

      // Transform to flat record
      const flatContext: Record<string, string> = {};
      entries.forEach(([componentId, entry]) => {
        const key = entry.metadata.scope ? `${entry.metadata.scope}:${componentId}` : componentId;
        flatContext[key] = entry.context;
      });

      // if (debugMode) {
      //   const totalSize = entries.reduce((sum, [, entry]) => sum + entry.metadata.size, 0);
      //   console.log(`[DutchieIntelligenceContext] Collected contexts (${entries.length} items, ${totalSize} chars)`, {
      //     filter,
      //     contexts: flatContext,
      //   });
      // }

      return flatContext;
    },
    [componentContexts, debugMode, currentPage]
  );

  const getContextMetadata = useCallback(() => componentContexts, [componentContexts]);

  // Calculate total cache size
  const getTotalCacheSize = useCallback(
    () => Object.values(componentContexts).reduce((total, entry) => total + entry.metadata.size, 0),
    [componentContexts]
  );

  // Get count of unique pages with contexts
  const getPageCount = useCallback(() => {
    const pages = new Set<string>();
    Object.values(componentContexts).forEach((entry) => {
      if (entry.metadata.page && !entry.metadata.global) {
        pages.add(entry.metadata.page);
      }
    });
    return pages.size;
  }, [componentContexts]);

  // Get cache statistics
  const getCacheStats = useCallback(() => {
    const pages = new Set<string>();
    Object.values(componentContexts).forEach((entry) => {
      if (entry.metadata.page && !entry.metadata.global) {
        pages.add(entry.metadata.page);
      }
    });
    return {
      totalSize: getTotalCacheSize(),
      pageCount: pages.size,
      pages: Array.from(pages),
    };
  }, [componentContexts, getTotalCacheSize]);

  // Evict contexts from oldest visited page (based on context timestamps)
  const evictOldestPage = useCallback(() => {
    setComponentContexts((prev) => {
      // Group contexts by page (excluding global contexts)
      const contextsByPage = new Map<string, ContextEntry[]>();

      Object.values(prev).forEach((entry) => {
        if (entry.metadata.page && !entry.metadata.global) {
          const existing = contextsByPage.get(entry.metadata.page) || [];
          existing.push(entry);
          contextsByPage.set(entry.metadata.page, existing);
        }
      });

      if (contextsByPage.size === 0) {
        return prev;
      }

      // Find oldest page by earliest timestamp of its contexts
      let oldestPage: string | null = null;
      let oldestTimestamp = Infinity;

      contextsByPage.forEach((contexts, page) => {
        const pageOldestTimestamp = Math.min(...contexts.map((c) => c.metadata.timestamp));
        if (pageOldestTimestamp < oldestTimestamp) {
          oldestTimestamp = pageOldestTimestamp;
          oldestPage = page;
        }
      });

      if (!oldestPage) {
        return prev;
      }

      // Remove contexts from oldest page
      const updated: Record<string, ContextEntry> = {};
      let _removed = 0;
      let _sizeFreed = 0;

      Object.entries(prev).forEach(([id, entry]) => {
        if (entry.metadata.global || entry.metadata.page !== oldestPage) {
          updated[id] = entry;
        } else {
          _removed += 1;
          _sizeFreed += entry.metadata.size;
        }
      });

      // if (debugMode) {
      //   console.log(
      //     `[DutchieIntelligenceContext] Evicted ${_removed} context(s) from page "${oldestPage}" (freed ${(_sizeFreed / 1024).toFixed(1)}KB)`
      //   );
      // }

      return updated;
    });
  }, [debugMode]);

  // Legacy method for backwards compatibility - now just evicts if needed
  const clearStaleContexts = useCallback(() => {
    const totalSize = getTotalCacheSize();
    const pageCount = getPageCount();

    // Only evict if we've visited many pages AND exceeded size limit
    if (pageCount > CONTEXT_CACHE_CONFIG.maxPagesBeforeSizeCheck && totalSize > CONTEXT_CACHE_CONFIG.maxTotalSize) {
      // if (debugMode) {
      //   console.log(
      //     `[DutchieIntelligenceContext] Cache size exceeded: ${(totalSize / 1024 / 1024).toFixed(2)}MB / ${(CONTEXT_CACHE_CONFIG.maxTotalSize / 1024 / 1024).toFixed(2)}MB`
      //   );
      // }

      evictOldestPage();
    }
  }, [getTotalCacheSize, getPageCount, evictOldestPage, debugMode]);

  // Mark contexts as referenced by the AI
  const markContextsAsReferenced = useCallback(
    (contextIds: string[]) => {
      setComponentContexts((prev) => {
        const updated = { ...prev };
        const now = Date.now();

        contextIds.forEach((id) => {
          if (updated[id]) {
            updated[id] = {
              ...updated[id],
              metadata: {
                ...updated[id].metadata,
                referencedByAI: true,
                referenceCount: (updated[id].metadata.referenceCount || 0) + 1,
                lastReferenced: now,
              },
            };
          }
        });

        // if (debugMode) {
        //   console.log(`[DutchieIntelligenceContext] Marked ${contextIds.length} context(s) as referenced by AI:`, contextIds);
        // }

        return updated;
      });
    },
    [debugMode]
  );

  // Debug logging functions
  const debugLog = useCallback(
    (
      level: DebugLogEntry['level'],
      category: DebugLogEntry['category'],
      message: DebugLogEntry['message'],
      data?: DebugLogEntry['data'],
      options?: {
        duration?: DebugLogEntry['duration'];
        parentLogId?: DebugLogEntry['parentLogId'];
        requestId?: DebugLogEntry['requestId'];
        status?: DebugLogEntry['status'];
      }
    ) => {
      const entry: DebugLogEntry = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        level,
        category,
        message,
        data,
        status: options?.status,
        requestId: options?.requestId,
        duration: options?.duration,
        parentLogId: options?.parentLogId,
      };

      // Use flushSync for logs with status (phase starts/ends) to show them immediately
      // This prevents batching and gives real-time feedback
      const shouldFlush = options?.status === 'start' || options?.status === 'success' || options?.status === 'error';

      if (shouldFlush) {
        flushSync(() => {
          setDebugLogs((prev) => {
            // Keep last 200 logs
            const updated = [...prev, entry];
            if (updated.length > 200) {
              return updated.slice(-200);
            }
            return updated;
          });
        });
      } else {
        setDebugLogs((prev) => {
          // Keep last 200 logs
          const updated = [...prev, entry];
          if (updated.length > 200) {
            return updated.slice(-200);
          }
          return updated;
        });
      }
    },
    []
  );

  const getDebugLogs = useCallback(() => debugLogs, [debugLogs]);

  const clearDebugLogs = useCallback(() => {
    setDebugLogs([]);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      isChatOpen,
      openIntelligenceWithMessage,
      closeIntelligence,
      initialMessage,
      clearInitialMessage,
      setComponentContext,
      removeComponentContext,
      getAllContext,
      getContextMetadata,
      currentPage,
      setCurrentPage,
      clearStaleContexts,
      getTotalCacheSize,
      getPageCount,
      getCacheStats,
      evictOldestPage,
      debugMode,
      setDebugMode,
      debugLog,
      getDebugLogs,
      clearDebugLogs,
      markContextsAsReferenced,
    }),
    [
      isChatOpen,
      openIntelligenceWithMessage,
      closeIntelligence,
      initialMessage,
      clearInitialMessage,
      setComponentContext,
      removeComponentContext,
      getAllContext,
      getContextMetadata,
      currentPage,
      setCurrentPage,
      clearStaleContexts,
      getTotalCacheSize,
      getPageCount,
      getCacheStats,
      evictOldestPage,
      debugMode,
      setDebugMode,
      debugLog,
      getDebugLogs,
      clearDebugLogs,
      markContextsAsReferenced,
    ]
  );

  return <DutchieIntelligenceContext.Provider value={contextValue}>{children}</DutchieIntelligenceContext.Provider>;
}

export function useDutchieIntelligenceContext() {
  const context = useContext(DutchieIntelligenceContext);
  if (context === undefined) {
    throw new Error('useDutchieIntelligenceContext must be used within a DutchieIntelligenceProvider');
  }
  return context;
}
